<template>
  <BaseContent class="flex flex-col overflow-hidden">
    <div
      ref="threadTitleRef"
      v-tooltip="isTitleOverflow ? threadTitle : ''"
      class="thread-title"
      :class="supportChat ? 'px-5 bg-[#F5F5F5] min-h-[37px] content-center' : ''"
    >
      {{ threadTitle }}
    </div>

    <BaseDiver v-if="!supportChat" />

    <BaseProgressBar v-if="pending" />

    <div
      ref="messageContainer"
      class="flex flex-col flex-grow overflow-auto mb-4 pr-4"
      :class="supportChat ? 'pl-4 overflow-x-hidden' : ''"
      @scroll="handleScroll"
    >
      <template
        v-for="(message, index) in messages"
        :key="message.id"
      >
        <div
          v-if="shouldShowDate(index)"
          class="text-center mb-2"
        >
          {{ getDate(message.created_at) }}
        </div>

        <Chatmessage
          v-if="messageContainer"
          class="message"
          :message="message"
          :container="messageContainer"
          :supportChat="route.path !== '/chatmessage'"
          :class="{
            'mb-1': nextMessageIsSameUser(index),
            'mb-4': !nextMessageIsSameUser(index)
          }"
          :userNameVisible="shouldShowUsername(index)"
        />
      </template>
    </div>

    <ChatmessageMessageInput
      :supportChat="route.path !== '/chatmessage'"
      @messageIsCreated="onMessageCreate"
    />
  </BaseContent>
</template>

<script setup lang="ts">
import { Chatmessage, useChatmessageModel } from '@/entities/chatmessage';
import { useSalespointModel } from '@/entities/salespoint';
import { ChatmessageMessageInput } from '@/features/chatmessage';
import { BaseContent, BaseDiver, BaseProgressBar } from '@/shared';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

defineProps<{
  supportChat?: boolean;
}>();

const route = useRoute();

const { salespoint } = storeToRefs(useSalespointModel());

const chatMessageModel = useChatmessageModel();
const { threadId, thread, messagesPageNumber } = storeToRefs(chatMessageModel);

const messages = computed(() =>
  chatMessageModel.messages ? [...chatMessageModel.messages]?.reverse() : null
);
const messageContainer = ref<HTMLDivElement | null>(null);
const previousScrollTop = ref(0);

const getMessagesPending = computed(() => chatMessageModel.getMessages.pending);
const pending = computed(() => getMessagesPending.value || waitImgLoading.value);
const waitImgLoading = ref<boolean>(false);

const threadTitleRef = ref<HTMLElement | null>(null);
const isTitleOverflow = ref(false);

watch(
  () => salespoint.value?.id,
  newSalespointId => {
    if (newSalespointId) {
      threadId.value = newSalespointId;
    }
  },
  { immediate: true }
);

const threadTitle = computed(() => {
  if (thread.value?.title) {
    return thread.value.title;
  }

  if (salespoint.value?.title) {
    return salespoint.value.title;
  }

  return '';
});

onMounted(() => {
  // Чтобы чат всегда был внизу при переходе на страницу
  scrollBottom();

  nextTick(checkTitleOverflow);

  window.addEventListener('resize', checkTitleOverflow);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkTitleOverflow);
});

watch(threadTitle, async () => {
  await nextTick();
  checkTitleOverflow();
});

watch(
  threadId,
  threadId => {
    if (threadId) {
      // При открытии чата и смене треда загружаем тред заново с последних сообщений
      getMessages(true);
    }
  },
  { immediate: true, flush: 'post' }
);

watch(
  messages,
  () => {
    if (messageContainer.value) {
      const messagesElements = messageContainer.value?.querySelectorAll('.message');
      const lastMessageElement = messagesElements?.[messagesElements.length - 2];
      if (lastMessageElement && isElementInViewport(lastMessageElement)) {
        // Если прокручено в низ, то при появлении нового сообщения опускает прокрутку в самый низ
        waitImg().then(() => {
          scrollBottom();
        });
      }
    }
  },
  { flush: 'post' }
);

function checkTitleOverflow() {
  if (!threadTitleRef.value) return;

  isTitleOverflow.value = threadTitleRef.value.scrollWidth > threadTitleRef.value.clientWidth;
}

async function waitImg() {
  waitImgLoading.value = true;

  const imgs = messageContainer.value?.querySelectorAll('img');

  if (imgs?.length) {
    const promises: Promise<unknown>[] = [];
    imgs.forEach(img => {
      const promise = new Promise(resolve => {
        if (img.complete) {
          resolve(null);
        }

        img.onload = () => {
          resolve(null);
        };
        img.onerror = () => {
          resolve(null);
        };
      });

      promises.push(promise);
    });

    await Promise.all(promises);
    waitImgLoading.value = false;
    return true;
  }

  waitImgLoading.value = false;
  return true;
}

function scrollBottom() {
  if (messageContainer.value) {
    messageContainer.value.scrollTop = messageContainer.value.scrollHeight;
  }
}

function nextMessageIsSameUser(index: number) {
  if (messages.value && messages.value.length > index + 1) {
    const currentMessage = messages.value[index];
    const nextMessage = messages.value[index + 1];

    if (currentMessage.role_user_msg === 'admin' && nextMessage.role_user_msg === 'admin') {
      return true;
    }

    return currentMessage.user_id === nextMessage.user_id;
  }

  return false;
}

function shouldShowUsername(index: number) {
  if (index === 0) return true;

  if (messages.value) {
    const currentMessage = messages.value[index];
    const prevMessage = messages.value[index - 1];

    if (currentMessage.role_user_msg === 'admin' && prevMessage.role_user_msg === 'admin') {
      return false;
    }

    return currentMessage.user_id !== prevMessage.user_id;
  }

  return false;
}

function shouldShowDate(index: number) {
  if (index === 0) return true;
  const prevMessage = messages.value?.[index - 1];
  const currentMessage = messages.value?.[index];

  if (currentMessage && prevMessage) {
    const currentMessageDay = new Date(currentMessage.created_at).toLocaleDateString();
    const prevMessageDateDay = new Date(prevMessage.created_at).toLocaleDateString();
    if (currentMessageDay !== prevMessageDateDay) {
      return true;
    }
  }

  return false;
}

function getDate(created_at: string) {
  return new Date(created_at).toLocaleDateString();
}

function handleScroll() {
  if (messageContainer.value) {
    const currentScrollTop = messageContainer.value.scrollTop;
    const isScrollingUp = currentScrollTop < previousScrollTop.value;

    // Проверяем, прокручивается ли вверх и достигнул ли определенного уровня прокрутки
    if (isScrollingUp && currentScrollTop <= 200) {
      getMessages();
    }

    // Обновляем предыдущее значение scrollTop
    saveScrollTop();
  }
}

function saveScrollTop() {
  if (messageContainer.value) {
    previousScrollTop.value = messageContainer.value.scrollTop;
  }
}

async function getMessages(reload = false) {
  // Проверяем первая ли страница треда загружается
  const isFirstPage = reload || messagesPageNumber.value === 0;

  const res = reload ? await chatMessageModel.reloadMessages() : await chatMessageModel.getMessages.post();

  if (res?._code === 0 && res._data) {
    // Если это была первая страница, то необходимо отправить упустить scroll в самый низ
    if (isFirstPage) {
      await waitImg();
      scrollBottom();
    }
  }
}

function isElementInViewport(el: Element) {
  const rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

function onMessageCreate() {
  waitImg().then(() => {
    scrollBottom();
  });
}
</script>

<style scoped>
.thread-title {
  flex-shrink: 0;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: clamp(12px, 2vw, 20px);
}
</style>
